import React, { useState } from 'react';
import { z } from 'zod';
import { Input } from '@/components/ui/input';
import { useFormContext, Controller } from 'react-hook-form';
import { allowMultipleUploads, QuestionData } from '../types';
import { useParams } from 'react-router-dom';
import { useClarificationsContext } from '@/contexts/ClarificationsContext';
import { useQueryClient } from '@tanstack/react-query';
import {
  fetchSubmissionDocuments,
  submissionDocumentsQueryKey,
  uploadSubmissionDocument,
} from '@/hooks/submissions/submissionDocumentApi';
import DocumentPreviewModal, {
  openDocument,
  type PreviewType,
} from '../DocumentPreviewModal';
import { MultiUploadField } from './MultiUploadField';
import {
  UploadCompleteBadge,
  UploadProgressIndicator,
} from './UploadProgressIndicator';
import { useUploadProgress } from '@/hooks/submission_forms/useUploadProgress';

interface UploadFieldProps {
  question: QuestionData;
  disabled?: boolean;
}

const SingleUploadField: React.FC<UploadFieldProps> = ({
  question,
  disabled,
}) => {
  const { control, getValues, setValue } = useFormContext();
  const { submissionId } = useParams();
  const queryClient = useQueryClient();
  const { refetch: refetchClarifications } = useClarificationsContext();
  const [fileName, setFileName] = useState<string | null>(
    getValues(question.slug)
  );
  const [isOpening, setIsOpening] = useState(false);
  const [freshViewUrl, setFreshViewUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewType, setPreviewType] = useState<PreviewType | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { status: uploadStatus, isInFlight, track } = useUploadProgress();

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const input = e.target;
    const file = input.files?.[0];
    if (!file || !submissionId) return;

    setError(null);
    try {
      await track(file.name, async onProgress => {
        const document = await uploadSubmissionDocument({
          submissionId,
          questionSlug: question.slug,
          file,
          onProgress,
        });
        // Only name the file once it's stored — until then it can't be opened,
        // and a failed upload must leave the previous file in place.
        setFreshViewUrl(null);
        setFileName(file.name);
        setValue(question.slug, file.name, { shouldValidate: true });
        return document;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload file');
      return;
    } finally {
      input.value = '';
    }

    // The file is stored at this point, so a failed refresh is not an upload
    // error — the lists catch up on their next refetch.
    try {
      await queryClient.invalidateQueries({
        queryKey: submissionDocumentsQueryKey(submissionId),
      });
      await refetchClarifications();
    } catch {
      // Intentionally ignored; see above.
    }
  }

  async function handleOpen() {
    if (!submissionId || !fileName) return;
    setIsOpening(true);
    try {
      const documents = await queryClient.fetchQuery({
        queryKey: submissionDocumentsQueryKey(submissionId),
        queryFn: () => fetchSubmissionDocuments(submissionId),
      });
      const match = documents.find(doc => doc.filename === fileName);
      if (!match?.view_url) return;
      const result = openDocument(fileName, match.view_url);
      if (result) {
        setFreshViewUrl(match.view_url);
        setPreviewType(result);
        setPreviewOpen(true);
      }
    } finally {
      setIsOpening(false);
    }
  }

  return (
    <div>
      <Controller
        name={question.slug}
        control={control}
        render={() => (
          <Input
            type="file"
            className="w-full cursor-pointer file:cursor-pointer"
            onChange={handleUpload}
            disabled={disabled || isInFlight}
          />
        )}
      />
      <UploadProgressIndicator status={uploadStatus} />
      {fileName && (
        <>
          <div className="mt-1 flex items-center gap-2">
            <button
              type="button"
              onClick={handleOpen}
              disabled={isOpening}
              className="text-sm text-primary hover:underline text-left disabled:opacity-50"
            >
              {isOpening ? 'Opening...' : fileName}
            </button>
            {uploadStatus?.phase === 'complete' && <UploadCompleteBadge />}
          </div>
          {previewType && (
            <DocumentPreviewModal
              open={previewOpen && !!freshViewUrl}
              onClose={() => {
                setPreviewOpen(false);
                setFreshViewUrl(null);
                setPreviewType(null);
              }}
              fileName={fileName}
              viewUrl={freshViewUrl ?? ''}
              previewType={previewType}
            />
          )}
        </>
      )}
      {error && <div className="text-red-500">{error}</div>}
    </div>
  );
};

const UploadFieldComponent: React.FC<UploadFieldProps> = props => {
  if (allowMultipleUploads(props.question)) {
    return <MultiUploadField {...props} />;
  }
  return <SingleUploadField {...props} />;
};

const createUploadSchema = (question: QuestionData): z.ZodTypeAny => {
  if (allowMultipleUploads(question)) {
    if (question.required) {
      return z.array(z.string()).min(1, 'This field is required');
    }
    return z.union([z.array(z.string()), z.literal('')]);
  }

  if (question.required) {
    return z.any().refine(val => typeof val === 'string' && val.length > 0, {
      message: 'This field is required',
    });
  }
  return z
    .any()
    .refine(
      val =>
        val === null ||
        val === undefined ||
        val === '' ||
        (typeof val === 'string' && val.length > 0),
      { message: 'Invalid value' }
    )
    .optional()
    .nullable();
};

export const UploadField = {
  component: UploadFieldComponent,
  createSchema: createUploadSchema,
  getDefaultValue: (question: QuestionData) =>
    allowMultipleUploads(question) ? [] : null,
};
